/**
 * Deep Links для популярных VPN приложений
 * Позволяет открывать подписку напрямую в VPN клиенте
 */

// Генерация deep link для разных VPN клиентов
export const generateDeepLink = (subscription, clientType) => {
  const encoded = encodeURIComponent(subscription);
  
  const deepLinks = {
    // Hiddify
    hiddify: `hiddify://import/${encoded}`,
    hiddifyAlt: `hiddify://add/${encoded}`,
    
    // V2RayNG (Android)
    v2rayng: `v2rayng://install-config?url=${encoded}`,
    v2rayngAlt: `v2rayng://add/${encoded}`,
    
    // Shadowrocket (iOS)
    shadowrocket: `shadowrocket://add/${encoded}`,
    shadowrocketSub: `shadowrocket://add/sub://${btoa(subscription)}`,
    
    // NekoRay / NekoBox
    nekoray: `nekoray://import?url=${encoded}`,
    nekobox: `nekobox://import/${encoded}`,
    
    // Streisand (iOS)
    streisand: `streisand://import?url=${encoded}`,
    
    // Happ
    happ: `happ://add/${encoded}`,
    
    // V2Box (iOS)
    v2box: `v2box://install-config?url=${encoded}`,
    
    // OneClick / FairVPN
    oneclick: `oneclick://add/${encoded}`,
    
    // Clash
    clash: `clash://install-config?url=${encoded}`,
    clashX: `clashx://install-config?url=${encoded}`,
    
    // Quantumult X (iOS)
    quantumult: `quantumult-x:///add-resource?remote-resource=${encoded}`,
    
    // ShadowLink
    shadowlink: `shadowlink://add/${encoded}`,
    
    // Universal (будет пытаться открыть в любом приложении)
    universal: subscription
  };
  
  return deepLinks[clientType] || deepLinks.universal;
};

// Проверка поддержки deep link (для iOS)
export const canOpenDeepLink = async (url) => {
  if (typeof window === 'undefined') return false;
  
  try {
    // На iOS можно попробовать открыть и проверить результат
    const timeout = new Promise((resolve) => setTimeout(() => resolve(false), 1000));
    const openPromise = new Promise((resolve) => {
      window.location.href = url;
      setTimeout(() => resolve(true), 500);
    });
    
    return await Promise.race([openPromise, timeout]);
  } catch (error) {
    return false;
  }
};

// Попытка открыть в приложении с fallback
export const openInApp = (subscription, clientType, onError) => {
  const deepLink = generateDeepLink(subscription, clientType);
  
  // Создаем невидимый iframe для попытки открытия
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = deepLink;
  document.body.appendChild(iframe);
  
  // Через 1 секунду проверяем, открылось ли приложение
  setTimeout(() => {
    document.body.removeChild(iframe);
    
    // Если приложение не открылось, показываем ошибку
    if (document.visibilityState === 'visible') {
      if (onError) {
        onError(`Приложение не установлено или не поддерживает deep links`);
      }
    }
  }, 1000);
};

// Список VPN клиентов с поддержкой deep links
export const DEEPLINK_CLIENTS = [
  {
    id: 'hiddify',
    name: 'Hiddify',
    icon: '🔷',
    platforms: ['Android', 'iOS', 'Windows', 'macOS'],
    deepLinkType: 'hiddify',
    storeLinks: {
      ios: 'https://apps.apple.com/app/hiddify/id6596777532',
      android: 'https://play.google.com/store/apps/details?id=com.hiddify.app',
      windows: 'https://github.com/hiddify/hiddify-next/releases',
      macos: 'https://github.com/hiddify/hiddify-next/releases'
    }
  },
  {
    id: 'v2rayng',
    name: 'V2RayNG',
    icon: '📱',
    platforms: ['Android'],
    deepLinkType: 'v2rayng',
    storeLinks: {
      android: 'https://play.google.com/store/apps/details?id=com.v2ray.ang'
    }
  },
  {
    id: 'shadowrocket',
    name: 'Shadowrocket',
    icon: '🚀',
    platforms: ['iOS'],
    deepLinkType: 'shadowrocket',
    storeLinks: {
      ios: 'https://apps.apple.com/app/shadowrocket/id932747118'
    }
  },
  {
    id: 'nekoray',
    name: 'NekoRay',
    icon: '🐱',
    platforms: ['Windows', 'Linux'],
    deepLinkType: 'nekoray',
    storeLinks: {
      windows: 'https://github.com/MatsuriDayo/nekoray/releases',
      linux: 'https://github.com/MatsuriDayo/nekoray/releases'
    }
  },
  {
    id: 'streisand',
    name: 'Streisand',
    icon: '⭐',
    platforms: ['iOS'],
    deepLinkType: 'streisand',
    storeLinks: {
      ios: 'https://apps.apple.com/app/streisand/id6450534064'
    }
  },
  {
    id: 'happ',
    name: 'Happ',
    icon: '😊',
    platforms: ['iOS', 'Android'],
    deepLinkType: 'happ',
    storeLinks: {
      ios: 'https://apps.apple.com/app/happ/id6476013940',
      android: 'https://play.google.com/store/apps/details?id=com.happ.android'
    }
  }
];

// Определение платформы пользователя
export const detectPlatform = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  
  if (/android/i.test(ua)) {
    return 'android';
  }
  
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) {
    return 'ios';
  }
  
  if (/Win/i.test(ua)) {
    return 'windows';
  }
  
  if (/Mac/i.test(ua)) {
    return 'macos';
  }
  
  if (/Linux/i.test(ua)) {
    return 'linux';
  }
  
  return 'unknown';
};

// Получить рекомендуемые приложения для текущей платформы
export const getRecommendedApps = () => {
  const platform = detectPlatform();
  return DEEPLINK_CLIENTS.filter(client => 
    client.platforms.some(p => p.toLowerCase() === platform)
  );
};

// Открыть в рекомендуемом приложении
export const openInRecommendedApp = (subscription, showToast) => {
  const recommended = getRecommendedApps();
  
  if (recommended.length === 0) {
    if (showToast) {
      showToast('Для вашей платформы нет рекомендованных приложений', 'info');
    }
    return false;
  }
  
  // Пробуем открыть в первом рекомендованном приложении
  const app = recommended[0];
  openInApp(subscription, app.deepLinkType, (error) => {
    if (showToast) {
      showToast(`${app.name} не установлен. Скачайте приложение из магазина.`, 'warning');
    }
  });
  
  return true;
};











